// Hook para consumir la API sin repetir el mismo cargando/error en cada pantalla.

import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError } from './client';

export type EstadoPedido = 'cargando' | 'listo' | 'error';

export interface Pedido<T> {
  datos: T | null;
  estado: EstadoPedido;
  error: string | null;
  recargar: () => void;
}

// Las dependencias las declara quien llama, como en useEffect: la función `traer`
// suele ser una flecha nueva en cada render y no sirve para decidir si repetir.
export function usePedido<T>(traer: () => Promise<T>, dependencias: readonly unknown[]): Pedido<T> {
  const [datos, setDatos] = useState<T | null>(null);
  const [estado, setEstado] = useState<EstadoPedido>('cargando');
  const [error, setError] = useState<string | null>(null);
  const [señal, setSeñal] = useState(0);
  const hayDatos = useRef(false);

  const recargar = useCallback(() => { setSeñal((n) => n + 1); }, []);

  useEffect(() => {
    let vigente = true;
    // Al recargar lo que ya está en pantalla se queda a la vista hasta que llega lo
    // nuevo: vaciarla para mostrar "Cargando" haría parpadear toda la pantalla.
    if (!hayDatos.current) setEstado('cargando');
    traer()
      .then((r) => {
        if (!vigente) return;
        hayDatos.current = true;
        setDatos(r); setError(null); setEstado('listo');
      })
      .catch((e: unknown) => {
        if (!vigente) return;
        setError(e instanceof ApiError ? e.message : 'Ocurrió un error inesperado');
        setEstado('error');
      });
    return () => { vigente = false; };
  }, [...dependencias, señal]);

  return { datos, estado, error, recargar };
}
